import { Poster } from "PosterEditor/poster/Poster";
import styles from "./PosterStage.module.css";
import { useEditorUI } from "PosterEditor/use-editor-ui";
import { EditorContextType } from "PosterEditor/PosterEditor";
import { usePosterProject } from "PosterEditor/model/poster-project-provider";
import { useSizePresets } from "PosterEditor/model/size-presets/size-preset";
import { useRef, useState, useEffect } from "react";
import { EXPANDED_HEIGHT_PERCENT } from "PosterEditor/sidebar/mobile-drawer";

interface PosterStageProps {
  onShowContextualEditor: (show: boolean) => void;
  contextType?: EditorContextType;
  isMobile?: boolean;
  isDrawerExpanded?: boolean;
  onDismissDrawer?: () => void;
}

export const PosterStage = (props: PosterStageProps) => {
  const showZoomControls = props.contextType !== "rendering" && !props.isMobile;
  const { project } = usePosterProject();
  const { screenSizeFromProject } = useSizePresets();
  const { editorUIState, setFocusedViewport } = useEditorUI();
  const editingStaticLabel = editorUIState.editingElement?.staticLabel;
  const containerRef = useRef<HTMLDivElement>(null);
  const posterContainerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  const screenSize = screenSizeFromProject(project) ?? { width: 0, height: 0 };

  const paddingScale = 0.92; // Scale down slightly to create visual padding

  useEffect(() => {
    const calculateScale = () => {
      if (!containerRef.current || !props.isMobile) {
        setScale(1);
        return;
      }

      const containerWidth = containerRef.current.clientWidth;
      // Use viewport height minus fixed elements (action bar ~60px + drawer ~80px + margins)
      // This gives us a stable height that doesn't change with poster size
      const availableHeight = window.innerHeight - 160;
      const posterWidth = screenSize.width;
      const posterHeight = screenSize.height;

      if (posterWidth > 0 && posterHeight > 0) {
        // Scale by whichever dimension is more constraining
        const widthScale = containerWidth / posterWidth;
        const heightScale = availableHeight / posterHeight;
        const fitScale = Math.min(widthScale, heightScale);
        const newScale = Math.min(fitScale * paddingScale, 1);
        setScale(newScale);
      } else {
        setScale(1);
      }
    };

    calculateScale();
    window.addEventListener("resize", calculateScale);
    return () => window.removeEventListener("resize", calculateScale);
  }, [props.isMobile, screenSize.width, screenSize.height]);

  // Handle tap-outside-poster to dismiss focused viewport on mobile
  useEffect(() => {
    if (!props.isMobile) return;
    if (!editorUIState.focusedViewportId) return;

    const container = containerRef.current;
    const posterContainer = posterContainerRef.current;
    if (!container || !posterContainer) return;

    let touchStartTime = 0;
    let touchStartPos = { x: 0, y: 0 };

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      touchStartTime = Date.now();
      touchStartPos = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    };

    const handleTouchEnd = (e: TouchEvent) => {
      // Only handle quick taps (not drags)
      const duration = Date.now() - touchStartTime;
      if (duration > 200) return;

      const touch = e.changedTouches[0];
      const dx = touch.clientX - touchStartPos.x;
      const dy = touch.clientY - touchStartPos.y;
      if (Math.sqrt(dx * dx + dy * dy) > 10) return;

      // Check if tap is outside the poster bounds
      const posterRect = posterContainer.getBoundingClientRect();
      const isOutsidePoster =
        touch.clientX < posterRect.left ||
        touch.clientX > posterRect.right ||
        touch.clientY < posterRect.top ||
        touch.clientY > posterRect.bottom;

      if (isOutsidePoster) {
        setFocusedViewport(undefined, undefined);
      }
    };

    container.addEventListener("touchstart", handleTouchStart, { passive: true });
    container.addEventListener("touchend", handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener("touchstart", handleTouchStart);
      container.removeEventListener("touchend", handleTouchEnd);
    };
  }, [props.isMobile, editorUIState.focusedViewportId, setFocusedViewport]);

  // Calculate zoom and transform when editing a label or focusing a viewport on mobile
  const calculateFocusTransform = () => {
    const defaultTransform = {
      scale: scale,
      translateX: 0,
      translateY: 0,
      transformOrigin: "top center",
    };

    if (!props.isMobile) {
      return defaultTransform;
    }

    const viewportHeight = window.innerHeight;
    const viewportWidth = containerRef.current?.clientWidth ?? window.innerWidth;
    const navbarHeight = 60;

    // Handle viewport focus (tap-to-zoom on gallery viewports)
    const focusedViewportRect = editorUIState.focusedViewportRect;
    if (editorUIState.focusedViewportId && focusedViewportRect) {
      const margin = 16;
      const availableWidth = viewportWidth - margin * 2;
      const availableHeight = viewportHeight - navbarHeight - margin * 2;

      // Calculate scale to fit viewport with margins
      const scaleToFitWidth = availableWidth / focusedViewportRect.width;
      const scaleToFitHeight = availableHeight / focusedViewportRect.height;
      const targetScale = Math.min(scaleToFitWidth, scaleToFitHeight, 1);

      // Calculate viewport center in poster coordinates
      const viewportCenterX = focusedViewportRect.x + focusedViewportRect.width / 2;
      const viewportCenterY = focusedViewportRect.y + focusedViewportRect.height / 2;

      // Screen center position
      const screenCenterX = viewportWidth / 2;
      const screenCenterY = navbarHeight + availableHeight / 2 + margin;

      // Calculate translation to center the viewport
      // After scaling, the poster's top-left is at (0, 0) in scaled coordinates
      // The viewport center in scaled coordinates is (viewportCenterX * targetScale, viewportCenterY * targetScale)
      // We need to translate so this point ends up at screen center
      const scaledViewportCenterX = viewportCenterX * targetScale;
      const scaledViewportCenterY = viewportCenterY * targetScale;

      // The poster is centered horizontally by flexbox, so we need to account for that
      const scaledPosterWidth = screenSize.width * targetScale;
      const posterLeftOffset = (viewportWidth - scaledPosterWidth) / 2;

      const translateX = screenCenterX - posterLeftOffset - scaledViewportCenterX;
      const translateY = screenCenterY - scaledViewportCenterY - navbarHeight;

      return {
        scale: targetScale,
        translateX,
        translateY,
        transformOrigin: "top center",
      };
    }

    // For label editing, require drawer to be expanded
    if (!props.isDrawerExpanded) {
      return defaultTransform;
    }

    const expandedDrawerHeight = viewportHeight * EXPANDED_HEIGHT_PERCENT;
    const drawerTopPosition = viewportHeight - expandedDrawerHeight;
    const availableHeight = drawerTopPosition - navbarHeight - 32; // 32px margin

    // If editing a static label, zoom in and center on it
    if (editingStaticLabel) {
      // Label position in poster coordinates (0-1 normalized)
      const labelX = editingStaticLabel.position.x;
      const labelY = editingStaticLabel.position.y;

      // Calculate zoom - be conservative to avoid cutting off wide labels
      // Labels near center (x ~0.5) can zoom more, labels near edges zoom less
      const distanceFromCenter = Math.abs(labelX - 0.5);
      // Zoom range: 1.3x for centered labels, down to 1.1x for edge labels
      const zoomMultiplier = 1.3 - distanceFromCenter * 0.4;
      const zoomedScale = Math.min(scale * zoomMultiplier, 1); // Don't exceed 1:1

      // Calculate where the label would be on screen after zoom
      // We want to position it in the vertical center of the available space
      const targetScreenY = navbarHeight + availableHeight * 0.4; // 40% down available space

      // Label's Y position in zoomed poster pixels
      const labelYInZoomedPoster = labelY * screenSize.height * zoomedScale;

      // Translate to put label at target position
      const translateY = targetScreenY - labelYInZoomedPoster - navbarHeight;

      // Calculate horizontal translation to keep label visible
      // The poster is centered by flexbox, so we need to shift it so the label is at screen center
      const zoomedWidth = screenSize.width * zoomedScale;
      const posterCenterX = zoomedWidth / 2;
      const labelXInZoomedPoster = labelX * zoomedWidth;

      // Distance from poster center to label (negative = label is left of center)
      const labelOffsetFromCenter = labelXInZoomedPoster - posterCenterX;

      // Shift poster opposite to label offset to center the label on screen
      let translateX = -labelOffsetFromCenter;

      // Clamp so poster edges don't go past viewport
      const maxTranslateX = Math.max(0, (zoomedWidth - viewportWidth) / 2);
      translateX = Math.max(-maxTranslateX, Math.min(maxTranslateX, translateX));

      return {
        scale: zoomedScale,
        translateX,
        translateY,
        transformOrigin: "top center",
      };
    }

    // Fallback: translate based on poster bottom (original behavior)
    const scaledHeight = screenSize.height * scale;
    const currentTopPosition = navbarHeight + 16;
    const posterBottomPosition = currentTopPosition + scaledHeight;
    if (posterBottomPosition > drawerTopPosition) {
      const overlap = posterBottomPosition - drawerTopPosition;
      return {
        ...defaultTransform,
        translateY: -(overlap + 16),
      };
    }

    return defaultTransform;
  };

  const focusTransform = calculateFocusTransform();

  const handleStageClick = () => {
    if (props.isMobile && props.isDrawerExpanded && props.onDismissDrawer) {
      props.onDismissDrawer();
    }
  };

  // Handle backdrop click - dismiss drawer and prevent event from reaching viewport
  const handleBackdropClick = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (props.onDismissDrawer) {
      props.onDismissDrawer();
    }
  };

  const scaledHeight = screenSize.height * focusTransform.scale;

  return (
    <div className={styles.mapContainer} ref={containerRef} onClick={handleStageClick}>
      {/* Backdrop overlay to capture touches when drawer is expanded - prevents viewport selection */}
      {props.isMobile && props.isDrawerExpanded && (
        <div
          className={styles.drawerBackdrop}
          onClick={handleBackdropClick}
          onTouchEnd={handleBackdropClick}
        />
      )}
      <div
        className={styles.scaledPosterContainer}
        style={{
          height: props.isMobile ? scaledHeight : "auto",
          transform: props.isMobile
            ? `translate(${focusTransform.translateX}px, ${focusTransform.translateY}px)`
            : "none",
          transition: "transform 0.3s ease-out",
        }}
      >
        <div
          ref={posterContainerRef}
          className={styles.mapAndLeftControlsContainer}
          style={
            props.isMobile
              ? {
                  width: screenSize.width,
                  height: screenSize.height,
                  transform: `scale(${focusTransform.scale})`,
                  transformOrigin: focusTransform.transformOrigin,
                  transition: "transform 0.3s ease-out",
                }
              : undefined
          }
        >
          {showZoomControls && <ZoomControl />}
          <Poster
            onShowContextualEditor={props.onShowContextualEditor}
            contextType={props.contextType}
            isMobile={props.isMobile}
          />
        </div>
      </div>
    </div>
  );
};

const ZoomControl = () => {
  const { zoomMapIn, zoomMapOut } = useEditorUI();
  return (
    <div className={styles.zoomControlContainer}>
      <button className={styles.zoomButton} onClick={() => zoomMapIn()}>
        +
      </button>
      <button className={styles.zoomButton} onClick={() => zoomMapOut()}>
        -
      </button>
    </div>
  );
};
